<template>
	<div
		class="g-selection-toolbar"
		:style="positionStyle"
		@mousedown.prevent
	>
		<div class="g-selection-toolbar__inner">
			<button
				type="button"
				class="g-selection-toolbar__btn"
				:class="{ 'is-active': states.bold }"
				title="粗体"
				@click="$emit('format', 'bold')"
			>B</button>
			<button
				type="button"
				class="g-selection-toolbar__btn g-selection-toolbar__btn--italic"
				:class="{ 'is-active': states.italic }"
				title="斜体"
				@click="$emit('format', 'italic')"
			>I</button>
			<button
				type="button"
				class="g-selection-toolbar__btn g-selection-toolbar__btn--underline"
				:class="{ 'is-active': states.underline }"
				title="下划线"
				@click="$emit('format', 'underline')"
			>U</button>

			<div
				class="g-selection-toolbar__size-wrap"
				:class="{ 'g-selection-toolbar__size-wrap--editing': activePanel === 'size' }"
			>
				<button
					v-if="activePanel !== 'size'"
					type="button"
					class="g-selection-toolbar__size-btn"
					title="字号"
					@mousedown.prevent
					@click="openSizePanel"
				>
					<span class="g-selection-toolbar__size-value">{{ currentFontSizeLabel }}</span>
				</button>
				<div v-else class="g-selection-toolbar__size-editor" @mousedown.stop>
					<input
						ref="fontSizeInputRef"
						type="number"
						class="g-selection-toolbar__size-input"
						v-model.number="fontSizeInput"
						min="12"
						max="72"
						placeholder="字号"
						@keydown.enter.prevent="onApplyFontSize"
						@keydown.esc.prevent="closeSizePanel"
						@blur="onSizeInputBlur"
					/>
					<span class="g-selection-toolbar__size-unit">px</span>
				</div>
			</div>

			<div class="g-selection-toolbar__color-wrap">
				<button
					type="button"
					class="g-selection-toolbar__color-btn"
					title="文字颜色"
					@click="toggleColorPicker('text')"
				>
					<span class="g-selection-toolbar__color-letter">A</span>
					<span
						class="g-selection-toolbar__color-bar"
						:style="{ background: currentTextColor }"
					></span>
				</button>
				<ColorPicker
					ref="colorPickerText"
					v-model="customTextColor"
					:hide-trigger="true"
					@change="onCustomTextColorInput"
				/>
			</div>

			<div class="g-selection-toolbar__color-wrap">
				<button
					type="button"
					class="g-selection-toolbar__color-btn g-selection-toolbar__color-btn--bg"
					title="背景色"
					@click="toggleColorPicker('bg')"
				>
					<span class="g-selection-toolbar__bg-icon"></span>
				</button>
				<ColorPicker
					ref="colorPickerBg"
					v-model="customBgColor"
					:hide-trigger="true"
					@change="onCustomBgColorInput"
				/>
			</div>

			<button
				v-for="item in alignOptions"
				:key="item.value"
				type="button"
				class="g-selection-toolbar__align-btn"
				:class="{ 'is-active': states.textAlign === item.value }"
				:title="item.label"
				@click="$emit('align', item.value)"
			>
				<span
					class="g-selection-toolbar__align-icon"
					:class="`g-selection-toolbar__align-icon--${item.value}`"
				></span>
			</button>
		</div>
		<div class="g-selection-toolbar__arrow"></div>
	</div>
</template>

<script>
		import { normalizeHexColor, parseFontSizeNumber } from '../utils/richText';
	import ColorPicker from './ColorPicker.vue';

	export default {
		name: 'GSelectionTextToolbar',
		components: { ColorPicker },
		props: {
			positionStyle: {
				type: Object,
				default: () => ({}),
			},
			states: {
				type: Object,
				default: () => ({
					bold: false,
					italic: false,
					underline: false,
					fontSize: null,
					textAlign: 'left',
				}),
			},
		},
		emits: ['format', 'text-color', 'bg-color', 'font-size', 'align'],
		data() {
			return {
				activePanel: null,
				currentTextColor: '#333333',
				customTextColor: '#333333',
				customBgColor: '#fff566',
				fontSizeInput: 18,
				alignOptions: [
					{ value: 'left', label: '左对齐' },
					{ value: 'center', label: '居中' },
					{ value: 'right', label: '右对齐' },
					{ value: 'justify', label: '两端对齐' },
				],
			};
		},
		computed: {
			currentFontSize() {
				return this.normalizeFontSize(this.states.fontSize);
			},
			currentFontSizeLabel() {
				if (!this.currentFontSize) return '字';
				return `${parseInt(this.currentFontSize, 10)}`;
			},
		},
		methods: {
			normalizeFontSize(size) {
				if (!size) return null;
				const value = String(size).trim().toLowerCase();
				const pxMatch = value.match(/^([\d.]+)px$/);
				if (pxMatch) return `${Math.round(parseFloat(pxMatch[1]))}px`;
				return null;
			},
			toggleColorPicker(type) {
				const ref = type === 'text' ? this.$refs.colorPickerText : this.$refs.colorPickerBg;
				ref?.toggle();
			},
			togglePanel(panel) {
				if (panel === 'size') {
					this.openSizePanel();
					return;
				}
				this.activePanel = this.activePanel === panel ? null : panel;
			},
			openSizePanel() {
				this.activePanel = 'size';
				this.fontSizeInput = parseFontSizeNumber(this.currentFontSize, 18);
				this.$nextTick(() => {
					this.$refs.fontSizeInputRef?.focus?.();
					this.$refs.fontSizeInputRef?.select?.();
				});
			},
			closeSizePanel() {
				this.activePanel = null;
			},
			closePanel() {
				this.activePanel = null;
				const textPicker = this.$refs.colorPickerText;
				const bgPicker = this.$refs.colorPickerBg;
				if (textPicker?.visible) textPicker.visible = false;
				if (bgPicker?.visible) bgPicker.visible = false;
			},
			hasOpenPanel() {
				if (this.activePanel !== null) return true;
				const textPicker = this.$refs.colorPickerText;
				const bgPicker = this.$refs.colorPickerBg;
				return textPicker?.visible || bgPicker?.visible;
			},
			onCustomTextColorInput(color) {
				this.customTextColor = color;
				this.currentTextColor = color;
				this.$emit('text-color', color);
			},
			onCustomBgColorInput(color) {
				this.customBgColor = color;
				this.$emit('bg-color', color);
			},
			onApplyFontSize() {
				if (this.activePanel !== 'size') return;
				const size = parseFontSizeNumber(this.fontSizeInput, 18);
				this.fontSizeInput = size;
				this.activePanel = null;
				this.$emit('font-size', `${size}px`);
			},
			onSizeInputBlur() {
				window.setTimeout(() => {
					if (this.activePanel !== 'size') return;
					this.onApplyFontSize();
				}, 120);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-selection-toolbar {
		position: fixed;
		z-index: 1000;
		transform: translateX(-50%);
		pointer-events: auto;
	}

	@toolbar-cell: 28px;
	@toolbar-gap: 4px;
	@toolbar-cols: 6;

	.g-selection-toolbar__inner {
		display: grid;
		grid-template-columns: repeat(@toolbar-cols, @toolbar-cell);
		gap: @toolbar-gap;
		justify-content: start;
		width: calc(@toolbar-cell * @toolbar-cols + @toolbar-gap * (@toolbar-cols - 1));
		max-width: calc(100vw - 24px);
		padding: 6px 8px;
		background: #fff;
		border-radius: 8px;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
		border: 1px solid #eceef2;
		box-sizing: content-box;
	}

	.g-selection-toolbar__arrow {
		width: 0;
		height: 0;
		margin: 0 auto;
		border-left: 7px solid transparent;
		border-right: 7px solid transparent;
		border-top: 7px solid #fff;
		filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.06));
		margin-top: -1px;
	}

	.g-selection-toolbar__btn {
		flex-shrink: 0;
		width: @toolbar-cell;
		min-width: @toolbar-cell;
		height: @toolbar-cell;
		padding: 0 6px;
		border: none;
		border-radius: 4px;
		background: transparent;
		font-size: 15px;
		font-weight: 700;
		color: #333;
		cursor: pointer;
		line-height: 28px;

		&:hover {
			background: #f5f6f8;
		}

		&.is-active {
			color: #285af9;
			background: rgba(40, 90, 249, 0.08);
		}

		&--italic {
			font-style: italic;
			font-weight: 600;
		}

		&--underline {
			text-decoration: underline;
			font-weight: 600;
		}
	}

	.g-selection-toolbar__color-wrap {
		position: relative;
		flex-shrink: 0;
		width: @toolbar-cell;
		height: @toolbar-cell;
	}

	.g-selection-toolbar__size-wrap {
		position: relative;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		width: @toolbar-cell;
		height: @toolbar-cell;

		&--editing {
			grid-column: span 2;
			width: calc(@toolbar-cell * 2 + @toolbar-gap);
		}
	}

	.g-selection-toolbar__align-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: @toolbar-cell;
		height: @toolbar-cell;
		padding: 0;
		border: none;
		border-radius: 4px;
		background: transparent;
		cursor: pointer;

		&:hover {
			background: #f5f6f8;
		}

		&.is-active {
			background: rgba(40, 90, 249, 0.08);

			.g-selection-toolbar__align-icon::before,
			.g-selection-toolbar__align-icon::after {
				background: #285af9;
			}
		}
	}

	.g-selection-toolbar__align-icon {
		position: relative;
		display: block;
		width: 16px;
		height: 12px;

		&::before,
		&::after {
			content: '';
			position: absolute;
			left: 0;
			height: 1px;
			border-radius: 1px;
			background: #333;
		}

		&::before {
			top: 1px;
			width: 14px;
		}

		&::after {
			top: 9px;
			width: 10px;
		}

		&--left::after {
			left: 0;
		}

		&--center {
			&::before,
			&::after {
				left: 50%;
				transform: translateX(-50%);
			}

			&::after {
				width: 11px;
			}
		}

		&--right {
			&::before,
			&::after {
				left: auto;
				right: 0;
			}

			&::after {
				width: 11px;
			}
		}

		&--justify {
			&::before,
			&::after {
				width: 16px;
			}
		}
	}

	.g-selection-toolbar__size-btn {
		width: @toolbar-cell;
		min-width: @toolbar-cell;
		height: @toolbar-cell;
		padding: 0 6px;
		border: none;
		border-radius: 4px;
		background: transparent;
		cursor: pointer;

		&:hover {
			background: #f5f6f8;
		}
	}

	.g-selection-toolbar__size-value {
		font-size: 13px;
		font-weight: 600;
		color: #333;
		line-height: 28px;
	}

	.g-selection-toolbar__size-editor {
		display: flex;
		align-items: center;
		gap: 4px;
		height: 28px;
		padding: 0 2px;
	}

	.g-selection-toolbar__size-input {
		width: 40px;
		height: 26px;
		padding: 0 6px;
		border: 1px solid #285af9;
		border-radius: 4px;
		font-size: 13px;
		color: #333;
		text-align: center;
		outline: none;
		background: #fff;
		box-sizing: border-box;

		&::-webkit-outer-spin-button,
		&::-webkit-inner-spin-button {
			-webkit-appearance: none;
			margin: 0;
		}

		-moz-appearance: textfield;
	}

	.g-selection-toolbar__size-unit {
		font-size: 12px;
		color: #666;
		line-height: 1;
		flex-shrink: 0;
	}

	.g-selection-toolbar__color-btn {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: @toolbar-cell;
		min-width: @toolbar-cell;
		height: @toolbar-cell;
		padding: 2px 4px;
		border: none;
		border-radius: 4px;
		background: transparent;
		cursor: pointer;

		&:hover {
			background: #f5f6f8;
		}
	}

	.g-selection-toolbar__color-letter {
		font-size: 15px;
		font-weight: 700;
		color: #333;
		line-height: 1;
	}

	.g-selection-toolbar__color-bar {
		display: block;
		width: 16px;
		height: 3px;
		margin-top: 2px;
		border-radius: 1px;
	}

	.g-selection-toolbar__bg-icon {
		display: block;
		width: 14px;
		height: 14px;
		background: linear-gradient(to bottom, #333 0 45%, #fff566 45% 100%);
		border: 1px solid #ddd;
		border-radius: 2px;
	}
</style>
